import React, { useMemo } from 'react';
import { X, SlidersHorizontal, Eye, EyeOff, CircleDollarSign, Package, Tag, Image as ImageIcon, Ruler } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import Rial from './Rial';

/**
 * Slide-out filters drawer for /admin/products. All facets compose with
 * AND — a product must match every active facet AND the existing
 * category-pill / search-box selection. Designed to feed into the bulk
 * edit/delete toolbar: narrow the set, then mass-edit only the matched rows.
 *
 * Filters value shape (kept flat & JSON-serialisable):
 *   {
 *     stock:        'in' | 'out' | null,
 *     vat:          'yes' | 'no'  | null,
 *     visibility:   'visible' | 'hidden' | null,
 *     image:        'has' | 'missing' | null,
 *     min_price:    number|null,  max_price: number|null,
 *     min_qty:      number|null,  max_qty:   number|null,
 *     uom:          string[]      // multi-select
 *     tag:          string[]      // multi-select
 *   }
 */

export const EMPTY_FILTERS = {
  stock: null, vat: null, visibility: null, image: null,
  min_price: null, max_price: null,
  min_qty: null,  max_qty: null,
  uom: [], tag: [],
};

/** Count active facets — used for the badge on the "Filters" pill. */
export const activeFilterCount = (f) => {
  if (!f) return 0;
  let n = 0;
  ['stock','vat','visibility','image'].forEach((k) => { if (f[k]) n++; });
  if (f.min_price != null || f.max_price != null) n++;
  if (f.min_qty   != null || f.max_qty   != null) n++;
  if ((f.uom || []).length) n++;
  if ((f.tag || []).length) n++;
  return n;
};

/** Apply the filter set to a product list. Pure, idempotent. */
export const applyFilters = (products, f) => {
  if (!f) return products;
  return products.filter((p) => {
    if (f.stock === 'in'  && !p.in_stock) return false;
    if (f.stock === 'out' &&  p.in_stock) return false;
    if (f.vat === 'yes' && !p.vat_applicable) return false;
    if (f.vat === 'no'  &&  p.vat_applicable) return false;
    if (f.visibility === 'visible' && p.is_visible === false) return false;
    if (f.visibility === 'hidden'  && p.is_visible !== false) return false;
    if (f.image === 'has'     && !p.image) return false;
    if (f.image === 'missing' &&  p.image) return false;
    if (f.min_price != null && p.price < f.min_price) return false;
    if (f.max_price != null && p.price > f.max_price) return false;
    if (f.min_qty != null && (p.min_qty ?? 1) < f.min_qty) return false;
    if (f.max_qty != null && (p.min_qty ?? 1) > f.max_qty) return false;
    if ((f.uom || []).length && !f.uom.includes(p.uom || 'ctn')) return false;
    if ((f.tag || []).length && !f.tag.includes(p.tag || '')) return false;
    return true;
  });
};

const TRI_STATE = (active, val) => active === val ? null : val; // tap-again to clear

const ProductFiltersDrawer = ({ open, onClose, value, onChange, uomOptions, tagOptions }) => {
  if (!open) return null;
  const v = { ...EMPTY_FILTERS, ...(value || {}) };
  const set = (patch) => onChange({ ...v, ...patch });
  const toggle = (arr, item) => {
    const next = arr.includes(item) ? arr.filter((x) => x !== item) : [...arr, item];
    return next;
  };

  return (
    <div className="fixed inset-0 z-[60]" data-testid="product-filters-drawer">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="absolute right-0 top-0 bottom-0 w-full max-w-md bg-white shadow-2xl flex flex-col">
        <div className="p-5 border-b border-neutral-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <SlidersHorizontal className="w-5 h-5 text-[#0078D4]" />
            <div>
              <h3 className="text-lg font-bold">Filters</h3>
              <p className="text-xs text-neutral-500">Narrow the table, then bulk-edit the matched rows.</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-neutral-100 rounded-full" data-testid="product-filters-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          <TriRow icon={Package} label="Stock" options={[
            { val: 'in',  label: 'In stock' },
            { val: 'out', label: 'Out of stock' },
          ]} value={v.stock} onChange={(x) => set({ stock: TRI_STATE(v.stock, x) })} testId="filter-stock" />

          <TriRow icon={CircleDollarSign} label="VAT" options={[
            { val: 'yes', label: 'Applicable (5%)' },
            { val: 'no',  label: 'No VAT' },
          ]} value={v.vat} onChange={(x) => set({ vat: TRI_STATE(v.vat, x) })} testId="filter-vat" />

          <TriRow icon={Eye} label="Visibility" options={[
            { val: 'visible', label: 'Visible', Icon: Eye },
            { val: 'hidden',  label: 'Hidden',  Icon: EyeOff },
          ]} value={v.visibility} onChange={(x) => set({ visibility: TRI_STATE(v.visibility, x) })} testId="filter-visibility" />

          <TriRow icon={ImageIcon} label="Image" options={[
            { val: 'has',     label: 'Has image' },
            { val: 'missing', label: 'Missing image' },
          ]} value={v.image} onChange={(x) => set({ image: TRI_STATE(v.image, x) })} testId="filter-image" />

          <RangeRow icon={CircleDollarSign} label={<>Price (<Rial />)</>} step="0.001" min="0"
            from={v.min_price} to={v.max_price}
            onFrom={(n) => set({ min_price: n })} onTo={(n) => set({ max_price: n })}
            testId="filter-price" placeholderFrom="0.000" placeholderTo="∞" />

          <RangeRow icon={Ruler} label="Min order qty" step="1" min="1"
            from={v.min_qty} to={v.max_qty}
            onFrom={(n) => set({ min_qty: n })} onTo={(n) => set({ max_qty: n })}
            testId="filter-minqty" placeholderFrom="1" placeholderTo="∞" />

          <MultiRow icon={Ruler} label="UOM" options={uomOptions}
            value={v.uom} onChange={(item) => set({ uom: toggle(v.uom, item) })}
            testId="filter-uom" />

          <MultiRow icon={Tag} label="Tag" options={tagOptions}
            value={v.tag} onChange={(item) => set({ tag: toggle(v.tag, item) })}
            testId="filter-tag" />
        </div>

        <div className="p-5 border-t border-neutral-200 flex gap-2">
          <Button onClick={() => onChange(EMPTY_FILTERS)} variant="outline" className="flex-1 h-11 rounded-full" data-testid="product-filters-clear">
            Clear all
          </Button>
          <Button onClick={onClose} className="flex-1 h-11 rounded-full bg-[#0078D4] hover:bg-[#005A9E] text-white font-semibold" data-testid="product-filters-apply">
            Done
          </Button>
        </div>
      </div>
    </div>
  );
};

const TriRow = ({ icon: Icon, label, options, value, onChange, testId }) => (
  <div>
    <div className="text-xs font-semibold text-neutral-700 uppercase tracking-wide flex items-center gap-1.5 mb-2">
      {Icon && <Icon className="w-3.5 h-3.5" />} {label}
    </div>
    <div className="flex gap-2">
      {options.map((o) => {
        const active = value === o.val;
        const ItemIcon = o.Icon;
        return (
          <button
            key={o.val}
            type="button"
            onClick={() => onChange(o.val)}
            className={`flex-1 h-10 rounded-lg border-2 text-sm font-semibold transition-colors flex items-center justify-center gap-1.5 ${
              active ? 'bg-[#F7FBFF] border-[#0078D4] text-[#0F172A]' : 'bg-white border-neutral-200 text-neutral-600 hover:border-neutral-300'
            }`}
            data-testid={`${testId}-${o.val}`}
          >
            {ItemIcon && <ItemIcon className="w-3.5 h-3.5" />} {o.label}
          </button>
        );
      })}
    </div>
  </div>
);

const RangeRow = ({ icon: Icon, label, from, to, onFrom, onTo, step, min, testId, placeholderFrom, placeholderTo }) => (
  <div>
    <div className="text-xs font-semibold text-neutral-700 uppercase tracking-wide flex items-center gap-1.5 mb-2">
      {Icon && <Icon className="w-3.5 h-3.5" />} {label}
    </div>
    <div className="flex items-center gap-2">
      <Input
        type="number" min={min} step={step}
        placeholder={placeholderFrom}
        value={from ?? ''}
        onChange={(e) => onFrom(e.target.value === '' ? null : parseFloat(e.target.value))}
        data-testid={`${testId}-from`}
      />
      <span className="text-neutral-400 text-sm">→</span>
      <Input
        type="number" min={min} step={step}
        placeholder={placeholderTo}
        value={to ?? ''}
        onChange={(e) => onTo(e.target.value === '' ? null : parseFloat(e.target.value))}
        data-testid={`${testId}-to`}
      />
    </div>
  </div>
);

const MultiRow = ({ icon: Icon, label, options, value, onChange, testId }) => {
  if (!options || options.length === 0) {
    return (
      <div>
        <div className="text-xs font-semibold text-neutral-700 uppercase tracking-wide flex items-center gap-1.5 mb-2">
          {Icon && <Icon className="w-3.5 h-3.5" />} {label}
        </div>
        <p className="text-xs text-neutral-400">No values to filter by.</p>
      </div>
    );
  }
  return (
    <div>
      <div className="text-xs font-semibold text-neutral-700 uppercase tracking-wide flex items-center gap-1.5 mb-2">
        {Icon && <Icon className="w-3.5 h-3.5" />} {label}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {options.map((opt) => {
          const active = (value || []).includes(opt);
          return (
            <button
              key={opt || '(none)'}
              type="button"
              onClick={() => onChange(opt)}
              className={`px-3 h-8 rounded-full border text-xs font-semibold transition-colors ${
                active ? 'bg-[#0078D4] text-white border-[#0078D4]' : 'bg-white text-neutral-700 border-neutral-300 hover:border-[#0078D4]'
              }`}
              data-testid={`${testId}-opt-${(opt || 'none').toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
            >
              {opt || '(no tag)'}
            </button>
          );
        })}
      </div>
    </div>
  );
};

/** Inline chip row that surfaces active filters above the table.
 * Click ✕ on a chip to clear that one facet without opening the drawer. */
export const ActiveFilterChips = ({ value, onChange }) => {
  const v = { ...EMPTY_FILTERS, ...(value || {}) };
  const chips = useMemo(() => {
    const out = [];
    const STOCK = { in: 'In stock', out: 'Out of stock' };
    const VAT = { yes: 'VAT 5%', no: 'No VAT' };
    const VIS = { visible: 'Visible', hidden: 'Hidden' };
    const IMG = { has: 'Has image', missing: 'Missing image' };
    if (v.stock)      out.push({ key: 'stock', label: STOCK[v.stock] });
    if (v.vat)        out.push({ key: 'vat', label: VAT[v.vat] });
    if (v.visibility) out.push({ key: 'visibility', label: VIS[v.visibility] });
    if (v.image)      out.push({ key: 'image', label: IMG[v.image] });
    if (v.min_price != null || v.max_price != null)
      out.push({ key: 'price', label: (<><Rial /> {v.min_price ?? '0'} – {v.max_price ?? '∞'}</>) });
    if (v.min_qty != null || v.max_qty != null)
      out.push({ key: 'qty', label: `Min qty ${v.min_qty ?? '1'} – ${v.max_qty ?? '∞'}` });
    (v.uom || []).forEach((u) => out.push({ key: `uom:${u}`, label: `UOM: ${u}` }));
    (v.tag || []).forEach((t) => out.push({ key: `tag:${t}`, label: `Tag: ${t || '(none)'}` }));
    return out;
  }, [v]);

  const clear = (key) => {
    if (key === 'price') return onChange({ ...v, min_price: null, max_price: null });
    if (key === 'qty')   return onChange({ ...v, min_qty: null, max_qty: null });
    if (key.startsWith('uom:')) return onChange({ ...v, uom: v.uom.filter((x) => x !== key.slice(4)) });
    if (key.startsWith('tag:')) return onChange({ ...v, tag: v.tag.filter((x) => x !== key.slice(4)) });
    onChange({ ...v, [key]: null });
  };

  if (chips.length === 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-1.5" data-testid="active-filter-chips">
      {chips.map((c) => (
        <button
          key={c.key}
          type="button"
          onClick={() => clear(c.key)}
          className="inline-flex items-center gap-1 pl-3 pr-2 h-7 rounded-full bg-[#DEECF9] text-[#0078D4] text-xs font-semibold hover:bg-[#BBDCF7]"
          data-testid={`chip-${c.key.replace(/[^a-z0-9]+/g, '-')}`}
        >
          {c.label}
          <X className="w-3 h-3" />
        </button>
      ))}
      <button
        type="button"
        onClick={() => onChange(EMPTY_FILTERS)}
        className="text-xs font-semibold text-neutral-500 hover:text-[#0078D4] ml-1"
        data-testid="clear-all-chips"
      >
        Clear all
      </button>
    </div>
  );
};

export default ProductFiltersDrawer;
